import { useHeaderVisibility } from "@hooks/useHeaderVisibility"
import { useNav } from "@hooks/useNav"
import { useSearch } from "@hooks/useSearch"
import { tv } from "@utils/tw"
import { Link, useRoute, useRouter } from "eddev/routing"
import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { ARROW_RIGHT, CLOSE, SEARCH } from "../Icons"
import { useIsMobile } from "@hooks/useViewportDerived"
import { useAppData } from "eddev/hooks"
import { twMerge } from "tailwind-merge"
import { ResponsiveImage } from "../ResponsiveImage"
import * as Icons from "@components/atoms/Icons"

type Props = {}

export function SearchOverlay(props: Props) {
  const search = useSearch()
  const [focused, setFocused] = useState(false)
  const router = useRouter()
  const { open: navOpen } = useNav()
  const { hidden, height: headerHeight } = useHeaderVisibility()

  const enableAISearch = useAppData((data) => data?.aiSearchSettings?.settings?.enable)
  const allPopularSearches = useAppData((data) => data?.aiSearchSettings?.settings?.popularSearches)
  const quicklinks = useAppData((data) => data?.aiSearchSettings?.settings?.quickLinks)
  const tile = useAppData((data) => data?.aiSearchSettings?.settings?.staticTile)

  const route = useRoute()

  useEffect(() => {
    if (route?.view === "search") {
      const term = route.query.q as string
      if (term) {
        search.setValue(term)
      }
    } else {
      search.setValue("")
    }
    search.setOpen(false)
  }, [route.pathname, route.query.q])

  const [placeholderIndex, setPlaceholderIndex] = useState(0)
  const placeholders = useMemo(() => {
    const placeholders = ["Ask anything..."]
    if (allPopularSearches) {
      placeholders.push(...allPopularSearches.map((s) => s?.text!).filter((text) => text.length < 51))
    }
    return placeholders
  }, [allPopularSearches])

  const popularSearches = useMemo(() => {
    return allPopularSearches?.sort((a, b) => Math.random() - 0.5).slice(0, 3)
  }, [allPopularSearches])

  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((i) => (i + 1) % placeholders.length)
    }, 3000)
    return () => clearInterval(interval)
  }, [placeholders.length])

  function doSearch(term: string) {
    if (term) {
      search.setValue(term)
      router?.navigate("/search?q=" + encodeURIComponent(term))
    }
    search.setOpen(false)
  }

  useEffect(() => {
    const el = document.documentElement

    if (search.open) {
      const scrollbarWidth = window.innerWidth - el.clientWidth
      el.style.overflow = "hidden"
      document.body.style.paddingRight = `${scrollbarWidth}px`
    } else {
      el.style.overflow = ""
      document.body.style.paddingRight = ""
    }

    return () => {
      el.style.overflow = ""
      document.body.style.paddingRight = ""
    }
  }, [search.open])

  const resultsRef = useRef<HTMLDivElement>(null)
  const [maxResultsHeight, setMaxResultsHeight] = useState<number | undefined>(undefined)

  useLayoutEffect(() => {
    if (!search.open) return

    const updateMaxHeight = () => {
      if (resultsRef.current) {
        const { top } = resultsRef.current.getBoundingClientRect()
        setMaxResultsHeight(window.innerHeight - top - 24)
      }
    }

    updateMaxHeight()
    window.addEventListener("resize", updateMaxHeight)
    return () => window.removeEventListener("resize", updateMaxHeight)
  }, [search.open])

  const isMobile = useIsMobile()
  const [ready, setReady] = useState(false)

  useEffect(() => {
    setTimeout(() => {
      setReady(true)
    }, 5000)
  }, [])

  if (!enableAISearch) return <></>

  return (
    <>
      <AnimatePresence initial={true}>
        <motion.div
          className={twMerge(
            "relative w-full mx-5 md:mx-0 md:fixed flex items-center z-[15] box-border",
            "[--search-width:91vw] [--search-height:42px] md:[--search-width:400px] md:[--search-height:48px]",
            "md:top-[calc(96px/2)] md:-translate-y-1/2 md:left-[calc(50%_-_var(--search-width)/2)] md:w-[var(--search-width)] md:h-[var(--search-height)]",
            navOpen && "hidden",
          )}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
        >
          <motion.div
            className="relative w-full h-[var(--search-height)]"
            animate={{ y: !isMobile && hidden ? -headerHeight : 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <div
              className={twMerge(
                "relative md:absolute top-0 left-0 w-full flex-col items-start justify-start bg-header-background box-content p-[12px] -m-[12px] rounded-lg overflow-hidden transition-colors duration-200",
                navOpen ? "hidden" : "flex",
                search.open && "md:bg-white",
              )}
            >
              <form
                className={twMerge(
                  "flex gap-3 relative bg-searchBg rounded-md items-center",
                  search.open ? "w-[calc(100%-42px-var(--spacing-3))] md:w-full" : "w-full",
                )}
                onSubmit={(e) => {
                  e.preventDefault()
                  doSearch(search.value)
                }}
              >
                <input
                  className={twMerge(
                    "w-full h-[var(--search-height)] relative outline-none appearance-none border-0 bg-transparent cursor-text text-dark-grey type-label-s pl-4 max-md:!text-[16px]",
                  )}
                  value={search.value}
                  onChange={(e) => search.setValue(e.currentTarget.value)}
                  onFocus={() => {
                    search.setOpen(true)
                    setFocused(true)
                  }}
                  onBlur={() => {
                    setFocused(false)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      search.setOpen(false)
                    }
                  }}
                  autoFocus={isMobile ? true : undefined}
                  enterKeyHint="search"
                  autoSave="off"
                />
                <button
                  type="submit"
                  className={twMerge(
                    "h-[calc(var(--search-height)*0.8)] mr-[calc(var(--search-height)*0.1)] text-black flex-none aspect-square flex items-center justify-center bg-transparent appearance-none rounded-[9px] transition-colors duration-200 pointer-events-none",
                    "data-[ready=true]:pointer-events-auto data-[ready=true]:bg-yellow data-[ready=true]:cursor-pointer",
                  )}
                  data-ready={search.value && focused && search.open}
                >
                  {SEARCH}
                </button>

                {search.value == "" && (
                  <AnimatePresence>
                    <motion.div
                      key={placeholderIndex}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute w-[calc(100%-50px)] md:w-full type-label-s inset-0 text-left md:text-center flex items-center justify-start md:justify-center pointer-events-none text-mid-grey px-4 md:px-6"
                    >
                      <span className="min-w-0 max-w-full whitespace-nowrap overflow-hidden leading-6 text-ellipsis">
                        {placeholders[placeholderIndex]}
                      </span>
                    </motion.div>
                  </AnimatePresence>
                )}
              </form>

              {search.open && (
                <button
                  className="md:hidden w-[var(--search-height)] min-w-[var(--search-height)] h-[var(--search-height)] bg-black rounded-md text-white flex items-center justify-center absolute right-[12px] top-[12px]"
                  onClick={() => search.setOpen(false)}
                >
                  {CLOSE}
                </button>
              )}

              <AnimatePresence>
                {search.open && (
                  <motion.div
                    ref={resultsRef}
                    className="relative flex flex-col gap-6 py-6 w-full overflow-x-hidden overflow-y-auto"
                    style={{ maxHeight: maxResultsHeight }}
                    initial={{ height: 0 }}
                    animate={{ height: "auto" }}
                    exit={{ height: 0 }}
                  >
                    {popularSearches?.length! > 0 && (
                      <div className="flex flex-col gap-[8px] w-full">
                        <div className="type-label-caps pb-0 pt-4 block">Popular Searches</div>
                        <div className="flex flex-col gap-[8px] w-full">
                          {popularSearches?.map((s, index) => (
                            <button key={index} className={itemButtonStyles()} onClick={() => doSearch(s?.text ?? "")}>
                              <span dangerouslySetInnerHTML={{ __html: s?.text ?? "" }} />
                              {SEARCH}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {quicklinks?.length! > 0 && (
                      <div className="flex flex-col gap-[8px] w-full">
                        <div className="type-label-caps pb-0 pt-4 block">Quick Links</div>
                        <div className="flex flex-col gap-[8px] w-full">
                          {quicklinks?.slice(0, 3)?.map((s, index) => (
                            <Link
                              key={index}
                              className={itemButtonStyles()}
                              href={s?.link?.url!}
                              target={s?.link?.target}
                              onClick={() => search.setOpen(false)}
                            >
                              <span dangerouslySetInnerHTML={{ __html: s?.link?.title ?? "" }} />
                              {ARROW_RIGHT}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                    {tile?.tileLink?.url && (
                      <Link
                        href={tile.tileLink.url}
                        target={tile.tileLink.target ? "_blank" : undefined}
                        className="border border-solid border-grey rounded-md bg-white p-3 md:p-2 flex items-center gap-6 hover:bg-yellow hover:border-yellow aaa-animate"
                      >
                        {tile.tileImage && (
                          <ResponsiveImage
                            {...tile.tileImage}
                            aspect={1}
                            className="w-[64px] aspect-square rounded-sm overflow-hidden"
                          />
                        )}
                        <div className="type-title-xs">{tile.tileLink.title}</div>
                        <i className="flex h-full items-center">{Icons["CARET_RIGHT"]}</i>
                      </Link>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
      <AnimatePresence>
        {search.open && (
          <motion.div
            className="hidden md:block fixed inset-0 bg-[rgba(0,0,0,0.2)] z-[14]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              search.setOpen(false)
            }}
          />
        )}
      </AnimatePresence>
    </>
  )
}

const itemButtonStyles = tv({
  base: "type-label-m text-dark-grey items-center justify-between bg-[rgba(0,0,0,0.02)] px-[1em] py-[0.8em] flex rounded-[8px] transition-colors duration-200 hover:bg-[rgba(0,0,0,0.05)]",
})
